import { createProp } from '@thyme/nashville/src/utils/designTokens'

export enum IconPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

/**
 *
 * USING NEW ICONS FROM EXISTING APPROVED SETS:
 *
 *    Icons from existing approved sets are fine to use.
 *
 * USING NEW ICON SETS:
 *
 *    When using a new icon set for the first time, we need to check the license to make
 *    sure we are allowed to use it in our applications.
 *
 *    To do so, go to `https://icon-sets.iconify.design/<icon-set-name>/` and hover over the
 *    license to see the usage details.
 *
 *    If allowed, add it to the list of allowed icon sets below.
 *
 */
export const AllowedIconSets = [
  'heroicons', // MIT
  'heroicons-solid', // MIT
  'heroicons-outline', // MIT
  'hugeicons', // MIT
  'ph', // MIT (Phosphor Icons)
  'fluent', // MIT
  'prime', // MIT
  'clarity', // MIT
  'tabler', // MIT
  'teenyicons', // MIT
  'bi', // MIT (Bootstrap Icons)
  'lucide', // ISC
  'logos', // CC0
  'healthicons', // MIT
]

export const IconifyIcons = {
  arrowRight: 'heroicons:arrow-right-20-solid',
  arrowRightCircle: 'heroicons:arrow-right-circle',
  backspace: 'heroicons:backspace',
  bell: 'heroicons-solid:bell',
  calendar: 'heroicons:calendar',
  daysCalendar: 'heroicons:calendar-days',
  check: 'heroicons:check',
  close: 'heroicons:x-mark',
  chat: 'heroicons:chat-bubble-left-ellipsis',
  chatSolid: 'heroicons:chat-bubble-oval-left-ellipsis-solid',
  textMessage: 'heroicons:chat-bubble-left-ellipsis-solid',
  chevronDown: 'heroicons:chevron-down-20-solid',
  chevronLeft: 'heroicons:chevron-left-20-solid',
  chevronRight: 'heroicons:chevron-right-20-solid',
  chevronUp: 'heroicons:chevron-up-20-solid',
  dialpad: 'fluent:dialpad-32-filled',
  download: 'heroicons:arrow-down-tray-20-solid',
  group: 'heroicons-solid:user-group',
  upload: 'heroicons:arrow-up-tray-20-solid',
  exclamationCircle: 'heroicons-solid:exclamation-circle',
  informationCircle: 'heroicons-outline:information-circle',
  link: 'heroicons:link-20-solid',
  linkSlash: 'heroicons:link-slash-20-solid',
  loading: 'prime:spinner',
  minimize: 'heroicons:minus-small-20-solid',
  pencil: 'heroicons:pencil',
  pencilSquare: 'heroicons-solid:pencil-square',
  phone: 'heroicons-solid:phone',
  phoneIncoming: 'heroicons-solid:phone-incoming',
  phoneOutgoing: 'heroicons-solid:phone-outgoing',
  phoneMissed: 'heroicons-solid:phone-x-mark',
  phoneLeftVoiceMail: 'heroicons:phone-arrow-up-right',
  newPhone: 'heroicons:phone',
  video: 'heroicons-solid:video-camera',
  add: 'heroicons:plus-solid',
  print: 'heroicons-outline:printer',
  refresh: 'heroicons-outline:refresh',
  search: 'heroicons:magnifying-glass-20-solid',
  sendMessage: 'heroicons:paper-airplane-20-solid',
  trash: 'heroicons:trash',
  wrench: 'clarity:wrench-line',
  listBullet: 'fluent:text-bullet-list-ltr-20-regular',
  bookOpen: 'heroicons:book-open-20-solid',
  chevronUpDown: 'fluent:chevron-up-down-24-filled',
  counterclockArrow: 'fluent:arrow-counterclockwise-16-filled',
  calendarDays: 'heroicons:calendar-days-20-solid',
  archive: 'heroicons:archive-box-arrow-down',
  blockedStatus: 'heroicons:minus-circle-20-solid',
  cancel: 'heroicons:x-mark-20-solid',
  cancelledStatus: 'heroicons:x-circle-20-solid',
  completedStatus: 'heroicons:check-circle-20-solid',
  outlineCompletedStatus: 'heroicons:check-circle',
  declinedTexting: 'tabler:message-circle-x',
  email: 'heroicons:envelope',
  emailSolid: 'heroicons:envelope-solid',
  errorNotification: 'heroicons:x-circle',
  infoNotification: 'heroicons:information-circle',
  inProgressStatus: 'tabler:progress',
  insurance: 'heroicons:shield-check',
  leftArrow: 'heroicons:chevron-left',
  meatball: 'heroicons:ellipsis-horizontal',
  notApprovedToSpeak: 'tabler:message-circle-off',
  notConsented: 'tabler:message-circle-question',
  onHoldStatus: 'heroicons:pause-circle-solid',
  openStatus: 'fluent:circle-12-regular',
  padlock: 'heroicons:lock-closed-20-solid',
  pathway: 'heroicons:book-open-solid',
  pointerRay: 'heroicons:cursor-arrow-rays-16-solid',
  libraryError: 'heroicons:book-open',
  sensitive: 'heroicons:eye-slash-solid',
  successNotification: 'heroicons:check-20-solid',
  taskName: 'heroicons:clipboard-20-solid',
  unarchive: 'heroicons:arrow-up-on-square',
  reattempt: 'heroicons:arrow-uturn-left-16-solid',
  filterFunnel: 'heroicons:funnel',
  language: 'heroicons:language-20-solid',
  barChart: 'heroicons:chart-bar-square-solid',
  exclamationTriangle: 'heroicons:exclamation-triangle',
  exclamationTriangleFilled: 'heroicons:exclamation-triangle-16-solid',
  phoneXMark: 'heroicons:phone-x-mark',
  phoneXMarkFilled: 'heroicons:phone-x-mark-solid',
  fax: 'heroicons:document-arrow-up',
  documentPlus: 'heroicons:document-plus-20-solid',
  checkCircle: 'heroicons:check-circle-16-solid',
  userCircle: 'teenyicons:user-circle-solid',
  userGroup: 'teenyicons:users-solid',
  bookOutline: 'heroicons-outline:book-open',
  buildingLibrary: 'heroicons:building-library',
  buildingStorefront: 'heroicons-solid:building-storefront',
  copy: 'heroicons-outline:square-2-stack',
  exclamationShield: 'heroicons:shield-exclamation-20-solid',
  checkShield: 'heroicons:shield-check-20-solid',
  notFoundSearch: 'lucide:search-x',
  penSparkle: 'fluent:pen-sparkle-32-regular',
  notAllowed: 'heroicons:no-symbol',
  sparkles: 'heroicons:sparkles-solid',
  sparklesOutline: 'heroicons:sparkles',
  thumbsUp: 'heroicons-solid:hand-thumb-up',
  thumbsDown: 'heroicons-solid:hand-thumb-down',
  thumbsUpOutline: 'heroicons:hand-thumb-up',
  thumbsDownOutline: 'heroicons:hand-thumb-down',
  personFillExclamation: 'bi:person-fill-exclamation',
  documentText: 'heroicons:document-text-20-solid',
  documentTextOutline: 'heroicons:document-text',
  documentTextSolid: 'heroicons:document-text-solid',
  reset: 'heroicons:arrow-path',
  googleDrive: 'logos:google-drive',
  bolt: 'heroicons-solid:bolt',
  chatBubble: 'heroicons:chat-bubble-left-20-solid',
  storefront: 'heroicons-solid:building-storefront',
  outlineEye: 'heroicons:eye',
  square2Stack: 'heroicons-outline:square-2-stack',
  arrowTopRightOnSquare: 'heroicons:arrow-top-right-on-square',
  rocketLaunch: 'heroicons:rocket-launch-20-solid',
  historyClock: 'prime:history',
  heartBeat: 'ph:heartbeat-fill',
  books: 'ph:books-fill',
  medal: 'ph:medal',
  inProgress: 'hugeicons:progress-03',
  thyme: 'hugeicons:plant-04',
  sti: 'healthicons:sti',
  nausea: 'healthicons:nausea',
  wind: 'ph:wind-fill',
  drop: 'ph:drop-fill',
  vomiting: 'healthicons:vomiting',
  notePadSparkle: 'fluent:notepad-sparkle-20-filled',
  muted: 'ph:microphone-slash-fill',
}

export enum SVGFileIconName {
  EmptyPriority = 'emptyPriority',
  HighPriority = 'highPriority',
  OptionalPriority = 'optionalPriority',
  RoutinePriority = 'routinePriority',
  UrgentPriority = 'urgentPriority',
}
export const AllIcons = { ...SVGFileIconName, ...IconifyIcons }

/**
 *
 * @param opts
 * @param opts.default
 * @param opts.required
 */
export function makeIconPositionProp(opts?: {
  default?: string
  required?: boolean
}) {
  return { iconPos: createProp(IconPosition, opts?.default, opts?.required) }
}

export const AllowedIcons = {
  ...AllIcons,
}
/**
 *
 * @param opts
 * @param opts.default
 * @param opts.required
 */
export function makeIconProp(opts?: { default?: string; required?: boolean }) {
  return { icon: createProp(null, opts?.default, opts?.required) }
}

export enum IconSize {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}
